import React from "react";
import { useNavigate } from "react-router-dom";
import Icon from "./Icon";
import { Chip } from "./primitives";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { useUnread } from "../context/UnreadContext";

/** Scroll container every routed page renders inside (mockup `.scroll`). Chats may skip it for its own panes. */
export function Page({ children, className = "" }) {
  return <div className={`scroll ${className}`.trim()}>{children}</div>;
}

/**
 * Gradient header band for top-level pages (DynamicHeader on mobile).
 * `right` is extra chips rendered before the Manage Club chip (shown to club leaders), the Super Admin
 * chip (super admins; the only way in below 820px, where the sidebar is hidden) and the bell
 * (red dot when there are unread notifications, or while the unread listener is failing, since the
 * count is then unknown rather than 0). `unread` overrides the UnreadContext value.
 * `back` ({ fallback, testId }) adds a phone-only back chip (`${testId}.back`) for pages that are a
 * sidebar destination on desktop but a stack page on phone (Settings, Friends, Notifications).
 * `globalChips={false}` drops Manage Club / Super Admin / bell, for pages whose own actions need
 * the room (Notifications: the bell would link to the page you are on).
 */
export function Band({ title, sub, right, unread, back, globalChips = true }) {
  const navigate = useNavigate();
  const goBack = useBack(back?.fallback);
  const { leadsAnyClub } = useData();
  const { isSuperAdmin } = useAuth();
  const ctx = useUnread();
  const count = unread ?? ctx.notifications;
  const unknown = unread == null && ctx.unreadError;
  return (
    <header className="band">
      <div className="band-in">
        {back ? (
          <Chip iconOnly glass className="band-back" aria-label="Back" data-testid={`${back.testId}.back`} onClick={goBack}><Icon name="left" /></Chip>
        ) : null}
        <div className="grow">
          <h1>{title}</h1>
          {sub ? <div className="sub">{sub}</div> : null}
        </div>
        {right}
        {globalChips && leadsAnyClub && (
          <Chip icon="shield" data-testid="header.manageClub" onClick={() => navigate("/manage")}>Manage Club</Chip>
        )}
        {globalChips && isSuperAdmin && (
          <Chip iconOnly aria-label="Super Admin" title="Super Admin" data-testid="header.superAdmin" onClick={() => navigate("/super")}>
            <Icon name="star" />
          </Chip>
        )}
        {globalChips && <Chip iconOnly dot={count > 0 || unknown} aria-label={unknown ? "Notifications (unread count unavailable)" : "Notifications"} data-testid="header.notifications" onClick={() => navigate("/notifications")}>
          <Icon name="bell" />
        </Chip>}
      </div>
    </header>
  );
}

/** navigate(-1), or `fallback` (default /clubs) when there is no in-app history. */
function useBack(fallback = "/clubs") {
  const navigate = useNavigate();
  return () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
}

/**
 * Back header for detail pages: navigate(-1), falling back to /clubs when there is no history.
 * The back chip's test id is `${testId}.back` (mobile: eventDetails.back, settings.back).
 */
export function BackBar({ title, right, fallback = "/clubs", locked = false, testId = "backBar" }) {
  // Kept inline (not useBack) so scripts/web-map sees the fallback navigate and maps those edges.
  const navigate = useNavigate();
  const back = () => {
    if (window.history.state && window.history.state.idx > 0) navigate(-1);
    else navigate(fallback, { replace: true });
  };
  return (
    <header className="band" style={{ paddingBlock: "18px 50px" }}>
      <div className="band-in">
        <Chip iconOnly glass aria-label="Back" data-testid={`${testId}.back`} disabled={locked} onClick={back}><Icon name="left" /></Chip>
        <h1 className="grow" style={{ fontSize: 22 }}>{title}</h1>
        {right}
      </div>
    </header>
  );
}
